import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { teamRolesApiRef } from '../plugin';
import { AssignTeamRoleParams } from '@agilelab/plugin-wb-team-roles-common';
import { TeamRoleAssignmentKindEnum } from '../types';
import { QUERY_KEY_TEAM_ROLE_ASSIGNMENTS } from '../constants';

type CreateTeamRoleAssignmentParams = AssignTeamRoleParams & {
  assignmentType: TeamRoleAssignmentKindEnum;
};

export const useCreateTeamRoleAssignment = (options?: {
  alertOnError: boolean;
}) => {
  const identityApi = useApi(identityApiRef);
  const teamRolesApi = useApi(teamRolesApiRef);
  const alertApi = useApi(customAlertApiRef);

  const queryClient = useQueryClient();

  const defaultOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_TEAM_ROLE_ASSIGNMENTS],
    });
    alertApi.post({
      severity: 'success',
      message: 'Team role assigned successfully',
    });
  };

  return useMutation({
    mutationFn: async ({
      assignmentType,
      ...params
    }: CreateTeamRoleAssignmentParams) => {
      const credentials = await identityApi.getCredentials();
      const payload = { ...params, options: credentials };

      if (assignmentType === TeamRoleAssignmentKindEnum.FULL) {
        return teamRolesApi.addAssignee(payload);
      }

      return teamRolesApi.addLimitedAssignee(payload);
    },
    onSuccess: defaultOnSuccess,
    onError: e => {
      if (options?.alertOnError === false) return;
      alertApi.post({
        severity: 'error',
        message: (e instanceof Error ? e : new Error('Something went wrong'))
          .message,
      });
    },
  });
};
